import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Faq, PricingPlan, Testimonial } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Experience } from '@/components/landing/experience'
import { AboutStatement } from '@/components/landing/about-statement'
import { FeaturesStack } from '@/components/landing/features-stack'
import { DashboardShowcase } from '@/components/landing/dashboard-showcase'
import { HowItWorks } from '@/components/landing/how-it-works'
import { Integrations } from '@/components/landing/integrations'
import { Testimonials } from '@/components/landing/testimonials'
import { PricingSection } from '@/components/sections/pricing-section'
import { FaqSection } from '@/components/sections/faq-section'

import {
    getFaqs,
    getPricingPlans,
    getTestimonials,
    safely
} from '@/lib/content'

interface HomeProps {
    testimonials: Array<Testimonial>
    plans: Array<PricingPlan>
    faqs: Array<Faq>
}

const Home: FC<HomeProps> = ({ testimonials, plans, faqs }): ReactNode => {
    return (
        <Layout>
            <Seo description='Veyra is the AI financial management platform that automates manual finance work, gives you real-time visibility into performance and helps you make faster, data-driven decisions.' />

            <Experience />
            <AboutStatement />
            <FeaturesStack />
            <DashboardShowcase />
            <HowItWorks />
            <Integrations />
            <Testimonials testimonials={testimonials} />
            <PricingSection plans={plans} />
            <FaqSection faqs={faqs} className='bg-paper' />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [testimonials, plans, faqs] = await Promise.all([
        safely(getTestimonials, [], 'testimonials'),
        safely(getPricingPlans, [], 'pricing plans'),
        safely(() => getFaqs({ homeOnly: true }), [], 'FAQs')
    ])

    return {
        props: { testimonials, plans, faqs },
        revalidate: 60
    }
}

export default Home
